// Below 0.0001 the value is indistinguishable from noise at any digit count we'd
// otherwise show — a fixed floor string reads better than "0.0000" or "0.0001".
// Exported so callers that collapse to "<0.0001%" can offer the exact value elsewhere
// (e.g. a tooltip) using the same threshold this module uses to trigger the collapse.
export const TINY_VALUE_FLOOR = 0.0001;
// Above this, 3 decimals is enough to show a non-zero digit; at or below it, a
// confirmed-tiny holding needs a 4th decimal to stay distinguishable from zero.
const THREE_DECIMAL_THRESHOLD = 0.0009;

/**
 * Adaptive precision: 3 decimals normally, 4 for very small values so they don't
 * collapse to "0.00", then trailing zeros are trimmed back down to a 2-decimal
 * floor (so "80.000" reads as "80.00", not padded with meaningless zeros).
 */
function formatAdaptivePercent(value: number): string {
    if (value > 0 && value < TINY_VALUE_FLOOR) return '<0.0001';

    let decimals = value > THREE_DECIMAL_THRESHOLD ? 3 : 4;
    let str = value.toFixed(decimals);
    while (decimals > 2 && str.endsWith('0')) {
        str = str.slice(0, -1);
        decimals -= 1;
    }
    return str;
}

export function formatShareholdingPercent(
    value: number | null | undefined,
    opts?: { prefix?: string; hideWhenNull?: boolean; nullText?: string },
): string {
    if (value == null || !Number.isFinite(value)) {
        if (opts?.hideWhenNull) return '';
        return opts?.nullText ?? 'NA';
    }
    return `${opts?.prefix ?? ''}${formatAdaptivePercent(value)}%`;
}

// `Designation • X.XX%` for surfaces (RP Details column, Credit Bureau) that show both
// together. No designation ⇒ falls back to the bare percentage/NA, never a dangling " • ".
// `hideWhenNull` (Related Party, Credit Bureau) drops the percentage — and its separator —
// entirely instead of showing "Designation • NA"; Management Profile keeps the NA default
// (or overrides it via `nullText`, e.g. "-").
export function formatDesignationWithShareholding(
    designation: string | null | undefined,
    value: number | null | undefined,
    opts?: { prefix?: string; hideWhenNull?: boolean; nullText?: string },
): string {
    const formattedPercent = formatShareholdingPercent(value, opts);
    if (!designation) return formattedPercent;
    if (!formattedPercent) return designation;
    return `${designation} • ${formattedPercent}`;
}
